/**
 * Article Form Page
 * Editor de artigo/documento (rich-text) — moldura no novo Design System:
 * header 3-zonas com breadcrumb + painel-editor central (RichTextEditor) intacto
 * + painéis laterais de propriedades (Publicação / Organização / Palavras-chave).
 * O núcleo (RichTextEditor/TipTap) NÃO é alterado.
 */

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useDatabase } from '../hooks/useDatabases';
import { useCreateDatabaseDocument, useDatabaseDocument, useUpdateDatabaseDocument, useDocumentSeries } from '../hooks/useDatabaseDocuments';
import { buildArticlePayload, defaultArticleForm, formFromDocument, preserveUnmanagedFields, type ArticleFormState } from './articlePayload';
import { Breadcrumb } from '@/shared/components/ui/Breadcrumb';
import { Button } from '@/shared/components/ui/Button';
import { Input } from '@/shared/components/ui/Input';
import { Select } from '@/shared/components/ui/Select';
import { Textarea } from '@/shared/components/ui/Textarea';
import { RichTextEditor } from '@/shared/components/ui/RichTextEditor';
import { MediaUploadInput } from '@/shared/components/ui/MediaUploadInput';
import { UploadInput } from '@/shared/components/ui/UploadInput';
import { formatDuration } from '@/shared/utils/formatDuration';
import { extractApiFieldErrors } from '@/shared/utils/apiFieldErrors';
import type {
  DatabaseDocumentData,
  DatabaseType,
  DocumentMediaType,
} from '@troia-v3/types';
import { Icon } from '@/shared/icons'
interface ArticleFormPageProps {
  documentId?: string;
}

const categoryOptions = [
  { value: 'article', label: 'Artigo' },
  { value: 'manual', label: 'Manual' },
  { value: 'faq', label: 'FAQ' },
  { value: 'policy', label: 'Política' },
  { value: 'guide', label: 'Guia' },
  { value: 'tutorial', label: 'Tutorial' },
  { value: 'report', label: 'Relatório' },
  { value: 'whitepaper', label: 'Whitepaper' },
  { value: 'case_study', label: 'Caso de Estudo' },
  { value: 'other', label: 'Outros' },
];

const statusOptions = [
  { value: 'draft', label: 'Rascunho' },
  { value: 'published', label: 'Publicado' },
  { value: 'archived', label: 'Arquivado' },
];

const languageOptions = [
  { value: 'pt-BR', label: 'Português (BR)' },
  { value: 'en-US', label: 'English (US)' },
  { value: 'es', label: 'Español' },
];

const mediaTypeOptions = [
  { value: '', label: 'Sem mídia' },
  { value: 'video', label: 'Vídeo' },
  { value: 'audio', label: 'Áudio' },
  { value: 'frame', label: 'Incorporado (YouTube, Vimeo, Loom...)' },
];

/** Painel lateral de propriedades — moldura DS (tokens do tema da página). */
const SideCard: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <div
    className="rounded-xl border overflow-hidden"
    style={{ backgroundColor: 'var(--theme-card-background)', borderColor: 'var(--theme-card-border)' }}
  >
    <div
      className="px-4 py-3 text-sm font-semibold"
      style={{ color: 'var(--theme-card-text)', borderBottom: '1px solid var(--theme-card-border)' }}
    >
      {title}
    </div>
    <div className="p-4 space-y-4">{children}</div>
  </div>
);

/** Rótulo de campo padronizado. */
const FieldLabel: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <label className="block text-sm font-medium mb-1.5" style={{ color: 'var(--theme-card-text)' }}>
    {children}
  </label>
);

export const ArticleFormPage: React.FC<ArticleFormPageProps> = ({ documentId }) => {
  const { id: databaseId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isEditing = !!documentId;

  const { data: database } = useDatabase(databaseId);
  const { data: seriesOptions = [] } = useDocumentSeries(databaseId);
  const { data: existingDocument, isLoading: isLoadingDocument } = useDatabaseDocument<DatabaseDocumentData>(
    documentId || '',
    isEditing
  );
  const createMutation = useCreateDatabaseDocument();
  const updateMutation = useUpdateDatabaseDocument<DatabaseDocumentData>();

  const [form, setForm] = useState<ArticleFormState>(defaultArticleForm);
  const [formLoaded, setFormLoaded] = useState(false);
  const [tagInput, setTagInput] = useState('');
  const [keywordInput, setKeywordInput] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Populate form with existing data when editing
  useEffect(() => {
    if (isEditing && existingDocument && !formLoaded) {
      setForm(formFromDocument(existingDocument.data));
      setFormLoaded(true);
    }
  }, [isEditing, existingDocument, formLoaded]);

  const handleBack = () => {
    if (isEditing && documentId) {
      navigate(`/databases/${databaseId}/documents/${documentId}`);
    } else {
      navigate(`/databases/${databaseId}`);
    }
  };

  if (isEditing && isLoadingDocument) {
    return (
      <div className="min-h-full flex items-center justify-center" style={{ backgroundColor: 'var(--theme-page-background)' }}>
        <div className="flex flex-col items-center gap-4">
          <Icon name="loader" className="w-8 h-8 animate-spin" style={{ color: 'var(--theme-primary)' }} />
          <span style={{ color: 'var(--theme-muted-text)' }}>Carregando documento...</span>
        </div>
      </div>
    );
  }

  const updateField = <K extends keyof ArticleFormState>(field: K, value: ArticleFormState[K]) => {
    setForm(prev => ({ ...prev, [field]: value }));
  };

  // Tags
  const addTag = () => {
    if (tagInput.trim() && !form.tags.includes(tagInput.trim())) {
      updateField('tags', [...form.tags, tagInput.trim()]);
      setTagInput('');
    }
  };

  const removeTag = (tag: string) => {
    updateField('tags', form.tags.filter(t => t !== tag));
  };

  // Keywords
  const addKeyword = () => {
    if (keywordInput.trim() && !form.keywords.includes(keywordInput.trim())) {
      updateField('keywords', [...form.keywords, keywordInput.trim()]);
      setKeywordInput('');
    }
  };

  const removeKeyword = (keyword: string) => {
    updateField('keywords', form.keywords.filter(k => k !== keyword));
  };

  // Save
  const handleSave = async () => {
    if (!form.title.trim()) {
      setError('Título é obrigatório');
      return;
    }
    if (!form.content.trim()) {
      setError('Conteúdo é obrigatório');
      return;
    }
    if (form.mediaType && !form.mediaUrl.trim()) {
      setError('Informe o link ou envie o arquivo da mídia');
      return;
    }

    setSaving(true);
    setError(null);

    try {
      const documentData = isEditing
        ? preserveUnmanagedFields(existingDocument?.data, buildArticlePayload(form))
        : buildArticlePayload(form);

      if (isEditing && documentId) {
        await updateMutation.mutateAsync({
          documentId,
          data: documentData,
        });
        navigate(`/databases/${databaseId}/documents/${documentId}`);
      } else {
        await createMutation.mutateAsync({
          databaseId: databaseId!,
          type: 'documents' as DatabaseType,
          data: documentData,
          metadata: { source: 'manual' },
        });
        navigate(`/databases/${databaseId}`);
      }
    } catch (err: unknown) {
      setError(extractApiFieldErrors(err).message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-full" style={{ backgroundColor: 'var(--theme-page-background)' }} data-testid="article-form-page">
      {/* Header 3-zonas (breadcrumb · —— · ações) */}
      <div
        className="sticky top-0 z-10"
        style={{
          backgroundColor: 'var(--theme-card-background)',
          borderBottom: '1px solid var(--theme-card-border)',
        }}
      >
        <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 md:px-8 py-3">
          <div className="flex items-center justify-between gap-3">
            {/* Zona 1 — Breadcrumb shared (F25-31; sem seta voltar) */}
            <Breadcrumb
              items={[
                { label: 'Bases de dados', onNavigate: () => navigate('/databases'), testid: 'article-breadcrumb-databases' },
                { label: database?.name || 'Base de dados', onNavigate: () => navigate(`/databases/${databaseId}`), testid: 'article-breadcrumb-database' },
                ...(isEditing
                  ? [{ label: form.title || 'Documento', onNavigate: handleBack, testid: 'article-breadcrumb-document' }]
                  : []),
                { label: isEditing ? 'Editar Artigo' : 'Novo Artigo', testid: 'article-breadcrumb-current' },
              ]}
              mobileLegend={`Bases de dados · ${database?.name || 'Base de dados'}`}
              data-testid="article-breadcrumb"
            />

            {/* Zona 3 — ações (único primário — Button shared, F01-03) */}
            <div className="flex items-center gap-2 flex-shrink-0">
              <Button
                variant="primary"
                icon="save"
                loading={saving}
                onClick={handleSave}
                data-testid="article-save-button"
              >
                Salvar
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Corpo — editor central + painéis laterais de propriedades */}
      <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 md:px-8 py-6">
        {error && (
          <div
            className="p-4 rounded-lg text-sm mb-4"
            data-testid="article-form-error"
            style={{
              backgroundColor: 'rgba(239,68,68,0.1)',
              color: 'var(--theme-error-color)',
              border: '1px solid var(--theme-error-color)',
            }}
          >
            {error}
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_340px] gap-4 items-start">
          {/* ==== NÚCLEO — editor central (título + RichTextEditor). NÃO reescrever. ==== */}
          <div
            className="rounded-xl border overflow-hidden"
            style={{ backgroundColor: 'var(--theme-card-background)', borderColor: 'var(--theme-card-border)' }}
          >
            <div className="px-5 pt-5 pb-2">
              <input
                value={form.title}
                onChange={(e) => updateField('title', e.target.value)}
                placeholder="Título do documento"
                data-testid="article-title-input"
                className="w-full bg-transparent border-none outline-none font-bold tracking-tight placeholder:opacity-40"
                style={{ fontSize: 26, lineHeight: 1.25, color: 'var(--theme-card-text)' }}
              />
            </div>
            <div style={{ backgroundColor: 'var(--theme-input-background)' }}>
              <RichTextEditor
                value={form.content}
                onChange={(html) => updateField('content', html)}
                placeholder="Escreva o conteúdo do artigo..."
                minHeight={400}
                features={{
                  bold: true,
                  italic: true,
                  underline: true,
                  textAlign: true,
                  bulletList: true,
                  orderedList: true,
                  heading: true,
                  link: true,
                  image: true,
                  attachment: false,
                  blockquote: true,
                }}
              />
            </div>
          </div>

          {/* ==== CHROME — painéis laterais de propriedades ==== */}
          <aside className="space-y-4">
            {/* Publicação */}
            <SideCard title="Publicação">
              <div>
                <FieldLabel>Status</FieldLabel>
                <Select
                  value={form.itemStatus}
                  onChange={(value) => updateField('itemStatus', value)}
                  options={statusOptions}
                />
              </div>
              <div>
                <FieldLabel>Autor</FieldLabel>
                <Input
                  value={form.author}
                  onChange={(e) => updateField('author', e.target.value)}
                  placeholder="Nome do autor"
                  data-testid="article-author-input"
                />
              </div>
              <div>
                <FieldLabel>Idioma</FieldLabel>
                <Select
                  value={form.language}
                  onChange={(value) => updateField('language', value)}
                  options={languageOptions}
                />
              </div>
            </SideCard>

            {/* Mídia da aula / post */}
            <SideCard title="Mídia">
              <div>
                <FieldLabel>Tipo</FieldLabel>
                <Select
                  value={form.mediaType}
                  onChange={(value) => setForm((prev) => ({ ...prev, mediaType: value as '' | DocumentMediaType, mediaUrl: '', duration: null }))}
                  options={mediaTypeOptions}
                  data-testid="article-media-type"
                />
              </div>
              {form.mediaType && (
                <div>
                  <FieldLabel>{form.mediaType === 'frame' ? 'Link ou código do iframe' : 'Arquivo'}</FieldLabel>
                  <MediaUploadInput
                    mediaType={form.mediaType}
                    value={form.mediaUrl}
                    onChange={(url) => updateField('mediaUrl', url)}
                    onDuration={(seconds) => updateField('duration', Math.round(seconds))}
                    testId="article-media"
                  />
                  <p className="text-xs mt-2" style={{ color: 'var(--theme-muted-text)' }}>
                    {form.mediaType === 'frame'
                      ? 'Fica gravado só o endereço do embed. O site monta o player.'
                      : 'Vídeo ou áudio até 2 GB. O envio vai direto para o armazenamento, com barra de progresso.'}
                    {form.duration ? ` Duração detectada: ${formatDuration(form.duration)}.` : ''}
                  </p>
                </div>
              )}
            </SideCard>

            {/* Capa */}
            <SideCard title="Capa">
              <div>
                <FieldLabel>Imagem</FieldLabel>
                <UploadInput
                  value={form.coverImage}
                  onChange={(url) => updateField('coverImage', url)}
                  accept="image/*"
                  folder="documents"
                  testId="article-cover"
                />
                {form.coverImage && (
                  <img src={form.coverImage} alt="" className="mt-3 w-full aspect-video object-cover rounded-[9px] border" style={{ borderColor: 'var(--theme-card-border)' }} data-testid="article-cover-preview" />
                )}
                <p className="text-xs mt-2" style={{ color: 'var(--theme-muted-text)' }}>
                  Aparece nos cards do site e como poster do player. Use 16:9, no mínimo 1280 × 720.
                </p>
              </div>
            </SideCard>

            {/* Organização */}
            <SideCard title="Organização">
              <div>
                <FieldLabel>Categoria</FieldLabel>
                <Select
                  value={form.category}
                  onChange={(value) => updateField('category', value)}
                  options={categoryOptions}
                />
              </div>
              <div>
                <FieldLabel>Trilha</FieldLabel>
                <Input
                  value={form.series}
                  onChange={(e) => updateField('series', e.target.value)}
                  placeholder="Ex.: Atendimento"
                  list="article-series-options"
                  data-testid="article-series-input"
                />
                <datalist id="article-series-options">
                  {seriesOptions.map((s) => (
                    <option key={s.name} value={s.name}>{`${s.count} ${s.count === 1 ? 'aula' : 'aulas'}`}</option>
                  ))}
                </datalist>
              </div>
              <div>
                <FieldLabel>Ordem na trilha</FieldLabel>
                <Input
                  type="number"
                  min={0}
                  step={1}
                  value={form.seriesOrder}
                  onChange={(e) => updateField('seriesOrder', e.target.value)}
                  placeholder="1"
                  data-testid="article-series-order-input"
                />
                <p className="text-xs mt-2" style={{ color: 'var(--theme-muted-text)' }}>
                  Nome da trilha e a posição desta aula dentro dela.
                </p>
              </div>
              <div>
                <FieldLabel>Resumo</FieldLabel>
                <Textarea
                  value={form.summary}
                  onChange={(e) => updateField('summary', e.target.value)}
                  rows={3}
                  placeholder="Breve resumo do documento"
                  data-testid="article-summary-input"
                />
              </div>
              <div>
                <FieldLabel>Tags</FieldLabel>
                {/* Pills + input tracejado "+ tag" inline (mockup) */}
                <div className="flex flex-wrap items-center gap-2">
                  {form.tags.map(tag => (
                    <span
                      key={tag}
                      className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium"
                      style={{ backgroundColor: 'rgba(139,92,246,0.1)', color: 'var(--theme-primary)' }}
                    >
                      {tag}
                      <button
                        type="button"
                        data-testid={`article-tag-remove-${tag}`}
                        onClick={() => removeTag(tag)}
                        className="hover:opacity-70 transition-opacity"
                      >
                        <Icon name="x" className="w-3 h-3" />
                      </button>
                    </span>
                  ))}
                  <input
                    value={tagInput}
                    onChange={(e) => setTagInput(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addTag(); } }}
                    onBlur={() => { if (tagInput.trim()) addTag(); }}
                    placeholder="+ tag"
                    data-testid="article-tag-input"
                    className="inline-flex items-center h-7 px-3 rounded-full text-xs border border-dashed bg-transparent outline-none placeholder:opacity-60"
                    style={{ borderColor: 'var(--theme-input-border)', color: 'var(--theme-card-text)', width: 96 }}
                  />
                </div>
              </div>
            </SideCard>

            {/* Palavras-chave (busca RAG) */}
            <SideCard title="Palavras-chave">
              <div>
                <FieldLabel>Termos de busca</FieldLabel>
                <div className="flex items-center gap-2">
                  <Input
                    value={keywordInput}
                    onChange={(e) => setKeywordInput(e.target.value)}
                    placeholder="Adicionar palavra-chave..."
                    data-testid="article-keyword-input"
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') { e.preventDefault(); addKeyword(); }
                    }}
                  />
                  <button
                    type="button"
                    data-testid="article-keyword-add"
                    onClick={addKeyword}
                    className="inline-flex items-center justify-center h-9 px-3 rounded-lg border text-sm font-medium transition-colors flex-shrink-0"
                    style={{
                      borderColor: 'var(--theme-input-border)',
                      color: 'var(--theme-card-text)',
                      backgroundColor: 'var(--theme-card-background)',
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = 'var(--theme-button-ghost-hover)'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'var(--theme-card-background)'; }}
                  >
                    <Icon name="plus" className="w-4 h-4" />
                  </button>
                </div>
                {form.keywords.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-3">
                    {form.keywords.map(keyword => (
                      <span
                        key={keyword}
                        className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium"
                        style={{ backgroundColor: 'rgba(139,92,246,0.1)', color: 'var(--theme-primary)' }}
                      >
                        {keyword}
                        <button
                          type="button"
                          data-testid={`article-keyword-remove-${keyword}`}
                          onClick={() => removeKeyword(keyword)}
                          className="hover:opacity-70 transition-opacity"
                        >
                          <Icon name="x" className="w-3 h-3" />
                        </button>
                      </span>
                    ))}
                  </div>
                )}
                <p className="text-xs mt-3" style={{ color: 'var(--theme-muted-text)' }}>
                  Ajuda o agente a encontrar este documento nas buscas.
                </p>
              </div>
            </SideCard>
          </aside>
        </div>
      </div>
    </div>
  );
};
